import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import type { StudentPayload } from './checkInTypes';

type BatchAddStudentsDialogProps = {
  open: boolean;
  grades: string[];
  rooms: string[];
  teachers: string[];
  /** Remaining seats on the plan; `null` means unlimited. */
  remainingSlots?: number | null;
  studentLimit?: number | null;
  onClose: () => void;
  onCreated: (students: StudentPayload[]) => void;
};

type WizardStep = 'upload' | 'preview' | 'confirm' | 'submitting' | 'result';

type SeparatorPreset = 'comma' | 'semicolon' | 'tab' | 'pipe' | 'custom';

type BatchRow = {
  key: string;
  firstName: string;
  middleName: string;
  lastName: string;
  grade: string;
  room: string;
  classTeacher: string;
  parent_guardian_1: string;
  parent_guardian_email_1: string;
  parent_guardian_phone_1: string;
  parent_guardian_2: string;
  parent_guardian_email_2: string;
  parent_guardian_phone_2: string;
};

type BatchFailure = {
  index: number;
  error: string;
};

const EXPECTED_COLUMNS = [
  'firstName',
  'middleName',
  'lastName',
  'grade',
  'room',
  'classTeacher',
  'parent_guardian_1',
  'parent_guardian_email_1',
  'parent_guardian_phone_1',
  'parent_guardian_2',
  'parent_guardian_email_2',
  'parent_guardian_phone_2',
] as const;

const HEADER_ALIASES: Record<string, (typeof EXPECTED_COLUMNS)[number]> = {
  firstname: 'firstName',
  first_name: 'firstName',
  'first name': 'firstName',
  middlename: 'middleName',
  middle_name: 'middleName',
  'middle name': 'middleName',
  lastname: 'lastName',
  last_name: 'lastName',
  'last name': 'lastName',
  grade: 'grade',
  room: 'room',
  classteacher: 'classTeacher',
  class_teacher: 'classTeacher',
  'class teacher': 'classTeacher',
  teacher: 'classTeacher',
  parent_guardian_1: 'parent_guardian_1',
  'parent guardian 1': 'parent_guardian_1',
  parentguardian1: 'parent_guardian_1',
  parent_guardian_2: 'parent_guardian_2',
  'parent guardian 2': 'parent_guardian_2',
  parentguardian2: 'parent_guardian_2',
  parent_guardian_email_1: 'parent_guardian_email_1',
  'parent guardian email 1': 'parent_guardian_email_1',
  parentguardianemail1: 'parent_guardian_email_1',
  parent_guardian_email_2: 'parent_guardian_email_2',
  'parent guardian email 2': 'parent_guardian_email_2',
  parentguardianemail2: 'parent_guardian_email_2',
  parent_guardian_phone_1: 'parent_guardian_phone_1',
  'parent guardian phone 1': 'parent_guardian_phone_1',
  parentguardianphone1: 'parent_guardian_phone_1',
  parent_guardian_phone_2: 'parent_guardian_phone_2',
  'parent guardian phone 2': 'parent_guardian_phone_2',
  parentguardianphone2: 'parent_guardian_phone_2',
};

const inputClassName =
  'mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const compactInputClassName =
  'w-full min-w-[6rem] rounded-lg border border-ink/15 bg-white px-2 py-1.5 font-sans text-sm text-ink focus:border-skysoft focus:outline-none focus:ring-1 focus:ring-skysoft/60';

const SEPARATOR_PRESETS: { id: SeparatorPreset; label: string; value: string }[] = [
  { id: 'comma', label: 'Comma (,)', value: ',' },
  { id: 'semicolon', label: 'Semicolon (;)', value: ';' },
  { id: 'tab', label: 'Tab', value: '\t' },
  { id: 'pipe', label: 'Pipe (|)', value: '|' },
  { id: 'custom', label: 'Custom', value: '' },
];

const matchOption = (raw: string, options: string[]): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const found = options.find((option) => option.toLowerCase() === trimmed.toLowerCase());
  return found ?? '';
};

const normalizeHeader = (header: string): string =>
  header.trim().replace(/^["']|["']$/g, '').toLowerCase();

const parseCsvLine = (line: string, separator: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === separator && !inQuotes) {
      fields.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  fields.push(current);
  return fields.map((field) => field.trim());
};

const splitCsvRows = (text: string): string[] => {
  const rows: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '""';
        i += 1;
      } else {
        inQuotes = !inQuotes;
        current += char;
      }
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i += 1;
      if (current.trim()) rows.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) rows.push(current);
  return rows;
};

const rowFingerprint = (row: BatchRow): string =>
  [row.firstName, row.middleName, row.lastName, row.grade, row.room]
    .map((value) => value.trim().toLowerCase())
    .join('\u0001');

const displayName = (row: BatchRow): string =>
  [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' ') || '(unnamed)';

let rowKeyCounter = 0;
const nextRowKey = () => {
  rowKeyCounter += 1;
  return `batch-row-${rowKeyCounter}`;
};

const BatchAddStudentsDialog = ({
  open,
  grades,
  rooms,
  teachers,
  remainingSlots = null,
  studentLimit = null,
  onClose,
  onCreated,
}: BatchAddStudentsDialogProps) => {
  const [step, setStep] = useState<WizardStep>('upload');
  const [separatorPreset, setSeparatorPreset] = useState<SeparatorPreset>('comma');
  const [customSeparator, setCustomSeparator] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileText, setFileText] = useState<string | null>(null);
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState(0);
  const [failures, setFailures] = useState<BatchFailure[]>([]);
  const [failedRows, setFailedRows] = useState<BatchRow[]>([]);

  const resolvedSeparator = useMemo(() => {
    if (separatorPreset === 'custom') return customSeparator;
    return SEPARATOR_PRESETS.find((item) => item.id === separatorPreset)?.value ?? ',';
  }, [customSeparator, separatorPreset]);

  const resetState = useCallback(() => {
    setStep('upload');
    setSeparatorPreset('comma');
    setCustomSeparator('');
    setFileName(null);
    setFileText(null);
    setRows([]);
    setParseError(null);
    setSubmitError(null);
    setCreatedCount(0);
    setFailures([]);
    setFailedRows([]);
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const duplicateGroups = useMemo(() => {
    const groups = new Map<string, number[]>();
    rows.forEach((row, index) => {
      const key = rowFingerprint(row);
      const existing = groups.get(key) ?? [];
      existing.push(index + 1);
      groups.set(key, existing);
    });
    return Array.from(groups.entries())
      .filter(([, indices]) => indices.length > 1)
      .map(([, indices]) => indices);
  }, [rows]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setParseError(null);
    if (!file) {
      setFileName(null);
      setFileText(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv') && file.type && !file.type.includes('csv') && file.type !== 'text/plain') {
      setParseError('Please upload a .csv file.');
      setFileName(null);
      setFileText(null);
      return;
    }
    try {
      const text = await file.text();
      setFileName(file.name);
      setFileText(text);
    } catch {
      setParseError('Could not read the selected file.');
      setFileName(null);
      setFileText(null);
    }
  };

  const parseFileToRows = useCallback((): BatchRow[] => {
    if (!fileText) {
      throw new Error('Please upload a CSV file.');
    }
    if (!resolvedSeparator) {
      throw new Error('Please choose a separator.');
    }
    if (separatorPreset === 'custom' && customSeparator.length !== 1) {
      throw new Error('Custom separator must be exactly 1 character.');
    }

    const lines = splitCsvRows(fileText);
    if (lines.length < 2) {
      throw new Error('CSV must include a header row and at least one data row.');
    }

    const headerFields = parseCsvLine(lines[0], resolvedSeparator).map(normalizeHeader);
    const columnIndex: Partial<Record<(typeof EXPECTED_COLUMNS)[number], number>> = {};

    headerFields.forEach((header, index) => {
      const mapped = HEADER_ALIASES[header];
      if (mapped && columnIndex[mapped] === undefined) {
        columnIndex[mapped] = index;
      }
    });

    if (
      columnIndex.firstName === undefined ||
      columnIndex.lastName === undefined ||
      columnIndex.parent_guardian_1 === undefined ||
      columnIndex.parent_guardian_email_1 === undefined ||
      columnIndex.parent_guardian_phone_1 === undefined
    ) {
      throw new Error(
        'CSV header must include firstName, lastName, parent_guardian_1, parent_guardian_email_1, and parent_guardian_phone_1 (other columns are optional).',
      );
    }

    const parsedRows: BatchRow[] = [];
    for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
      const fields = parseCsvLine(lines[lineIndex], resolvedSeparator);
      const read = (column: (typeof EXPECTED_COLUMNS)[number]) => {
        const index = columnIndex[column];
        if (index === undefined) return '';
        return (fields[index] ?? '').trim();
      };

      parsedRows.push({
        key: nextRowKey(),
        firstName: read('firstName'),
        middleName: read('middleName'),
        lastName: read('lastName'),
        grade: matchOption(read('grade'), grades),
        room: matchOption(read('room'), rooms),
        classTeacher: matchOption(read('classTeacher'), teachers),
        parent_guardian_1: read('parent_guardian_1'),
        parent_guardian_email_1: read('parent_guardian_email_1'),
        parent_guardian_phone_1: read('parent_guardian_phone_1'),
        parent_guardian_2: read('parent_guardian_2'),
        parent_guardian_email_2: read('parent_guardian_email_2'),
        parent_guardian_phone_2: read('parent_guardian_phone_2'),
      });
    }

    if (parsedRows.length === 0) {
      throw new Error('No student rows found in the CSV.');
    }
    if (parsedRows.length > 500) {
      throw new Error('CSV must contain 500 rows or fewer.');
    }

    return parsedRows;
  }, [
    customSeparator,
    fileText,
    grades,
    resolvedSeparator,
    rooms,
    separatorPreset,
    teachers,
  ]);

  const goToPreview = () => {
    setParseError(null);
    try {
      setRows(parseFileToRows());
      setStep('preview');
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Failed to parse CSV');
    }
  };

  const updateRow = (key: string, patch: Partial<BatchRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const previewValidationError = useMemo(() => {
    if (rows.length === 0) return 'Add at least one student row to continue.';
    if (remainingSlots !== null && remainingSlots <= 0) {
      return studentLimit === null
        ? 'Student limit reached. Upgrade your plan to add more students.'
        : `Student limit reached (${studentLimit}). Upgrade your plan to add more students.`;
    }
    if (remainingSlots !== null && rows.length > remainingSlots) {
      return `This batch has ${rows.length} students, but your plan only allows ${remainingSlots} more (limit ${studentLimit}). Remove rows or upgrade billing.`;
    }
    const missing = rows
      .map((row, index) => ({ row, index: index + 1 }))
      .filter(
        ({ row }) =>
          !row.firstName.trim() ||
          !row.lastName.trim() ||
          !row.parent_guardian_1.trim() ||
          !row.parent_guardian_email_1.trim() ||
          !row.parent_guardian_phone_1.trim(),
      );
    if (missing.length > 0) {
      return `Rows ${missing.map((item) => item.index).join(', ')} are missing required fields (first name, last name, parent/guardian 1, email 1, or phone 1).`;
    }
    return null;
  }, [remainingSlots, rows, studentLimit]);

  const goToConfirm = () => {
    if (previewValidationError) return;
    setStep('confirm');
  };

  const handleSubmit = async () => {
    if (previewValidationError) {
      setSubmitError(previewValidationError);
      return;
    }
    setSubmitError(null);
    setStep('submitting');
    const snapshot = rows.map((row) => ({ ...row }));
    try {
      const res = await fetch('/api/students/batch', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          students: snapshot.map((row) => ({
            firstName: row.firstName.trim(),
            middleName: row.middleName.trim(),
            lastName: row.lastName.trim(),
            grade: row.grade.trim(),
            room: row.room.trim(),
            classTeacher: row.classTeacher.trim(),
            parent_guardian_1: row.parent_guardian_1.trim(),
            parent_guardian_email_1: row.parent_guardian_email_1.trim(),
            parent_guardian_phone_1: row.parent_guardian_phone_1.trim(),
            parent_guardian_2: row.parent_guardian_2.trim(),
            parent_guardian_email_2: row.parent_guardian_email_2.trim(),
            parent_guardian_phone_2: row.parent_guardian_phone_2.trim(),
          })),
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Batch create failed (${res.status})`);
      }
      const data = (await res.json()) as {
        created: StudentPayload[];
        failed: BatchFailure[];
        createdCount: number;
      };
      setCreatedCount(data.createdCount ?? data.created.length);
      setFailures(data.failed ?? []);
      setFailedRows(
        (data.failed ?? [])
          .map((failure) => snapshot[failure.index])
          .filter(Boolean),
      );
      if (data.created.length > 0) {
        onCreated(data.created);
      }
      setStep('result');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Batch create failed');
      setStep('confirm');
    }
  };

  const stepLabel =
    step === 'upload'
      ? 'Step 1 of 3 · Upload'
      : step === 'preview'
        ? 'Step 2 of 3 · Preview'
        : step === 'confirm'
          ? 'Step 3 of 3 · Confirm'
          : step === 'submitting'
            ? 'Creating students…'
            : 'Done';

  const canClose = step !== 'submitting';

  const duplicateWarning =
    duplicateGroups.length > 0 ? (
      <div
        className="rounded-xl bg-amber-100/80 px-3 py-2 font-sans text-sm font-semibold text-ink"
        role="alert"
      >
        <p>Duplicate records detected (case-insensitive match on first name, middle name, last name, grade, and room):</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 font-medium">
          {duplicateGroups.map((indices) => (
            <li key={indices.join('-')}>
              Rows {indices.join(', ')} — {displayName(rows[indices[0] - 1])}
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => {
          if (canClose) onClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <Dialog.Title className="font-display text-xl font-semibold text-ink">
                      Batch add students
                    </Dialog.Title>
                    <p className="mt-1 font-sans text-sm text-ink/60">{stepLabel}</p>
                    {remainingSlots !== null ? (
                      <p className="mt-1 font-sans text-sm text-ink/70">
                        Your plan allows {remainingSlots} more student
                        {remainingSlots === 1 ? '' : 's'}
                        {studentLimit !== null ? ` (limit ${studentLimit})` : ''}.
                      </p>
                    ) : null}
                  </div>
                  {canClose ? (
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-full px-3 py-1.5 font-sans text-sm font-semibold text-ink/70 hover:bg-ink/5"
                    >
                      Close
                    </button>
                  ) : null}
                </div>

                {step === 'upload' ? (
                  <div className="mt-5 space-y-5">
                    <div className="rounded-2xl border-2 border-ink/10 bg-paper/50 px-4 py-3 font-sans text-sm text-ink/75">
                      <p className="font-semibold text-ink">Expected CSV schema</p>
                      <p className="mt-2">
                        Header row required. Columns:{' '}
                        <code className="rounded bg-white px-1.5 py-0.5 text-xs font-semibold text-ink">
                          {EXPECTED_COLUMNS.join(', ')}
                        </code>
                      </p>
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        <li>
                          <span className="font-semibold">firstName</span>,{' '}
                          <span className="font-semibold">lastName</span>,{' '}
                          <span className="font-semibold">parent_guardian_1</span>,{' '}
                          <span className="font-semibold">parent_guardian_email_1</span>, and{' '}
                          <span className="font-semibold">parent_guardian_phone_1</span> are
                          required.
                        </li>
                        <li>
                          <span className="font-semibold">middleName</span>,{' '}
                          <span className="font-semibold">grade</span>,{' '}
                          <span className="font-semibold">room</span>,{' '}
                          <span className="font-semibold">classTeacher</span>,{' '}
                          <span className="font-semibold">parent_guardian_2</span>,{' '}
                          <span className="font-semibold">parent_guardian_email_2</span>, and{' '}
                          <span className="font-semibold">parent_guardian_phone_2</span> may be
                          empty.
                        </li>
                        <li>
                          <code className="rounded bg-white px-1 text-xs">teacher</code> is accepted as an
                          alias for <code className="rounded bg-white px-1 text-xs">classTeacher</code>.
                        </li>
                      </ul>
                    </div>

                    <label className="block max-w-md">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Separator
                      </span>
                      <select
                        className={inputClassName}
                        value={separatorPreset}
                        onChange={(e) =>
                          setSeparatorPreset(e.target.value as SeparatorPreset)
                        }
                      >
                        {SEPARATOR_PRESETS.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {separatorPreset === 'custom' ? (
                      <label className="block max-w-xs">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Custom separator (1 character)
                        </span>
                        <input
                          className={inputClassName}
                          value={customSeparator}
                          maxLength={1}
                          onChange={(e) => setCustomSeparator(e.target.value.slice(0, 1))}
                          placeholder="e.g. :"
                        />
                      </label>
                    ) : null}

                    <label className="block max-w-md">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        CSV file
                      </span>
                      <input
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        className={`${inputClassName} file:mr-3 file:rounded-full file:border-0 file:bg-skysoft/70 file:px-3 file:py-1.5 file:font-sans file:text-sm file:font-semibold file:text-ink`}
                        onChange={(e) => void handleFileChange(e)}
                      />
                      {fileName ? (
                        <p className="mt-2 font-sans text-sm text-ink/65">Selected: {fileName}</p>
                      ) : null}
                    </label>

                    {parseError ? (
                      <p
                        className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                        role="alert"
                      >
                        {parseError}
                      </p>
                    ) : null}

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={goToPreview}
                        className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}

                {step === 'preview' ? (
                  <div className="mt-5 space-y-4">
                    {duplicateWarning}

                    {previewValidationError ? (
                      <p
                        className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                        role="alert"
                      >
                        {previewValidationError}
                      </p>
                    ) : null}

                    <div className="max-h-[50vh] overflow-auto rounded-2xl border-2 border-ink/10">
                      <table className="min-w-full divide-y divide-ink/10 text-left">
                        <thead className="sticky top-0 bg-paper/95">
                          <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                            <th className="px-3 py-2">#</th>
                            <th className="px-3 py-2">First name</th>
                            <th className="px-3 py-2">Middle name</th>
                            <th className="px-3 py-2">Last name</th>
                            <th className="px-3 py-2">Grade</th>
                            <th className="px-3 py-2">Room</th>
                            <th className="px-3 py-2">Teacher</th>
                            <th className="px-3 py-2">Guardian 1</th>
                            <th className="px-3 py-2">Email 1</th>
                            <th className="px-3 py-2">Phone 1</th>
                            <th className="px-3 py-2">Guardian 2</th>
                            <th className="px-3 py-2">Email 2</th>
                            <th className="px-3 py-2">Phone 2</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ink/10">
                          {rows.map((row, index) => (
                            <tr key={row.key} className="align-top font-sans text-sm text-ink">
                              <td className="px-3 py-2 font-semibold text-ink/55">{index + 1}</td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.firstName}
                                  onChange={(e) =>
                                    updateRow(row.key, { firstName: e.target.value })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.middleName}
                                  onChange={(e) =>
                                    updateRow(row.key, { middleName: e.target.value })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.lastName}
                                  onChange={(e) =>
                                    updateRow(row.key, { lastName: e.target.value })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  className={compactInputClassName}
                                  value={row.grade}
                                  onChange={(e) =>
                                    updateRow(row.key, { grade: e.target.value })
                                  }
                                >
                                  <option value="">—</option>
                                  {grades.map((item) => (
                                    <option key={item} value={item}>
                                      {item}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  className={compactInputClassName}
                                  value={row.room}
                                  onChange={(e) =>
                                    updateRow(row.key, { room: e.target.value })
                                  }
                                >
                                  <option value="">—</option>
                                  {rooms.map((item) => (
                                    <option key={item} value={item}>
                                      {item}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  className={compactInputClassName}
                                  value={row.classTeacher}
                                  onChange={(e) =>
                                    updateRow(row.key, { classTeacher: e.target.value })
                                  }
                                >
                                  <option value="">—</option>
                                  {teachers.map((item) => (
                                    <option key={item} value={item}>
                                      {item}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_1}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_1: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_email_1}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_email_1: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_phone_1}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_phone_1: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_2}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_2: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_email_2}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_email_2: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  className={compactInputClassName}
                                  value={row.parent_guardian_phone_2}
                                  onChange={(e) =>
                                    updateRow(row.key, {
                                      parent_guardian_phone_2: e.target.value,
                                    })
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => removeRow(row.key)}
                                  className="rounded-full px-3 py-1.5 text-sm font-semibold text-doghouse ring-1 ring-doghouse/30 hover:bg-doghouse/10"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-between gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setStep('upload')}
                        className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={goToConfirm}
                        disabled={Boolean(previewValidationError)}
                        className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}

                {step === 'confirm' || step === 'submitting' ? (
                  <div className="mt-5 space-y-4">
                    {step === 'submitting' ? (
                      <div className="rounded-2xl border-2 border-ink/10 bg-paper/50 px-4 py-10 text-center">
                        <p className="font-display text-lg font-semibold text-ink">
                          Creating students…
                        </p>
                        <p className="mt-2 font-sans text-sm text-ink/65">
                          Please wait while we insert {rows.length} record
                          {rows.length === 1 ? '' : 's'}.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="font-sans text-sm text-ink/70">
                          Review the {rows.length} student{rows.length === 1 ? '' : 's'} below,
                          then submit to create them. Rows that fail will not block the rest.
                        </p>

                        {duplicateWarning}

                        {submitError ? (
                          <p
                            className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                            role="alert"
                          >
                            {submitError}
                          </p>
                        ) : null}

                        <div className="max-h-[50vh] overflow-auto rounded-2xl border-2 border-ink/10">
                          <table className="min-w-full divide-y divide-ink/10 text-left">
                            <thead className="sticky top-0 bg-paper/95">
                              <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">First name</th>
                                <th className="px-3 py-2">Middle name</th>
                                <th className="px-3 py-2">Last name</th>
                                <th className="px-3 py-2">Grade</th>
                                <th className="px-3 py-2">Room</th>
                                <th className="px-3 py-2">Teacher</th>
                                <th className="px-3 py-2">Guardian 1</th>
                                <th className="px-3 py-2">Email 1</th>
                                <th className="px-3 py-2">Phone 1</th>
                                <th className="px-3 py-2">Guardian 2</th>
                                <th className="px-3 py-2">Email 2</th>
                                <th className="px-3 py-2">Phone 2</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-ink/10">
                              {rows.map((row, index) => (
                                <tr key={row.key} className="font-sans text-sm text-ink">
                                  <td className="px-3 py-2 font-semibold text-ink/55">
                                    {index + 1}
                                  </td>
                                  <td className="px-3 py-2">{row.firstName || '—'}</td>
                                  <td className="px-3 py-2">{row.middleName || '—'}</td>
                                  <td className="px-3 py-2">{row.lastName || '—'}</td>
                                  <td className="px-3 py-2">{row.grade || '—'}</td>
                                  <td className="px-3 py-2">{row.room || '—'}</td>
                                  <td className="px-3 py-2">{row.classTeacher || '—'}</td>
                                  <td className="px-3 py-2">{row.parent_guardian_1 || '—'}</td>
                                  <td className="px-3 py-2">
                                    {row.parent_guardian_email_1 || '—'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.parent_guardian_phone_1 || '—'}
                                  </td>
                                  <td className="px-3 py-2">{row.parent_guardian_2 || '—'}</td>
                                  <td className="px-3 py-2">
                                    {row.parent_guardian_email_2 || '—'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.parent_guardian_phone_2 || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex justify-between gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setStep('preview')}
                            className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
                          >
                            Submit
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}

                {step === 'result' ? (
                  <div className="mt-5 space-y-4">
                    <div className="rounded-2xl border-2 border-ink/10 bg-paper/50 px-4 py-4 font-sans text-sm text-ink">
                      <p className="font-display text-lg font-semibold">
                        Created {createdCount} student{createdCount === 1 ? '' : 's'}.
                      </p>
                      {failures.length > 0 ? (
                        <div className="mt-3">
                          <p className="font-semibold text-doghouse">
                            {failures.length} row{failures.length === 1 ? '' : 's'} failed to
                            insert:
                          </p>
                          <ul className="mt-2 list-disc space-y-1 pl-5 text-ink/80">
                            {failures.map((failure, failureIndex) => {
                              const matched = failedRows[failureIndex];
                              const label = matched
                                ? `Row ${failure.index + 1} (${displayName(matched)})`
                                : `Row ${failure.index + 1}`;
                              return (
                                <li key={`${failure.index}-${failure.error}`}>
                                  {label}: {failure.error}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        <p className="mt-2 text-ink/65">All rows were created successfully.</p>
                      )}
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BatchAddStudentsDialog;
